// ═══════════════════════════════════════════════════════════════════════════════
// AI Analysis Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { LEVEL1_DEFAULTS, ALLOWED_IMAGE_TYPES } from '../constants/index.js';
import { receiptDataSchema } from './receipt.schema.js';
import { emptyOrNumberSchema } from './common.schema.js';
const geminiLooseStringSchema = z
    .union([z.string(), z.null(), z.undefined()])
    .transform((value) => (typeof value === 'string' ? value : ''));
const geminiLooseQtySchema = z
    .union([z.number(), z.string(), z.literal(''), z.null(), z.undefined()])
    .transform((value) => (value === null || value === undefined ? '' : value));
const geminiLooseAmountSchema = z
    .union([emptyOrNumberSchema, z.null(), z.undefined()])
    .transform((value) => (value === null || value === undefined ? '' : value));
// ── Loose Gemini item/receipt (model may omit or shorten fields) ──
const geminiReceiptItemLooseSchema = z.object({
    item: geminiLooseStringSchema.default(''),
    qty: geminiLooseQtySchema.default(''),
    amount: geminiLooseAmountSchema.default(''),
});
const geminiReceiptDataLooseSchema = z.object({
    company: geminiLooseStringSchema.default(''),
    shop: geminiLooseStringSchema.default(''),
    date: geminiLooseStringSchema.default(''),
    currency: geminiLooseStringSchema.default(''),
    totalAmount: geminiLooseAmountSchema.default(''),
    items: z.array(geminiReceiptItemLooseSchema).default([]),
    remark: geminiLooseStringSchema.default(''),
});
// ── Gemini Receipts Envelope Schema (parsed before strict normalization) ──
export const geminiReceiptsEnvelopeSchema = z.object({
    receipts: z.array(geminiReceiptDataLooseSchema).min(1, 'At least one receipt is required'),
});
// ── Analyze Result Schema (per file) ──
export const analyzeResultSchema = z.object({
    fileName: z.string(),
    success: z.boolean(),
    data: z.union([z.array(receiptDataSchema), z.literal('')]),
    error: z.string().default(''),
    storedImageUrl: z.string().optional(),
    storedImageUrlForDisplay: z.string().optional(),
});
// ── Analyze Response Schema (API response) ──
export const analyzeResponseSchema = z.object({
    totalFiles: z.number().int().min(0),
    results: z.array(analyzeResultSchema),
});
// ── File Upload Validation Schema ──
export const fileUploadValidationSchema = z.object({
    fileName: z.string().min(1, 'File name is required'),
    mimeType: z.enum(ALLOWED_IMAGE_TYPES, {
        error: 'Unsupported image format',
    }),
    size: z.number()
        .max(LEVEL1_DEFAULTS.MAX_FILE_SIZE_BYTES, 'File size exceeds maximum allowed'),
});
// ── Analyze Request Files Validation ──
export const analyzeRequestFilesSchema = z.array(fileUploadValidationSchema)
    .min(1, 'At least one file is required')
    .max(LEVEL1_DEFAULTS.MAX_RECEIPT_FILES_PER_REQUEST, `Maximum ${LEVEL1_DEFAULTS.MAX_RECEIPT_FILES_PER_REQUEST} files per request`);
// ── AI Rate Limit Error Schema ──
export const aiRateLimitErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    retryAt: z.string(),
    dailyLimit: z.number(),
    usedToday: z.number(),
});
//# sourceMappingURL=ai.schema.js.map